import React, { useContext } from 'react';
import Responsive from '@amzn/meridian/responsive';

/**
 * DeviceLayoutContext — common responsive/layout context (ported from v1).
 * Uses Meridian's Responsive to derive isMobile + spacing shorthands from breakpoints.
 */
export interface DeviceLayout {
  isMobile: boolean;
  spacingInset_shorthand2: string;
  spacingInset_shorthand3: string;
  spacingInset_shorthand4: string;
}

const DEFAULT_LAYOUT: DeviceLayout = {
  isMobile: false,
  spacingInset_shorthand2: 'small medium',
  spacingInset_shorthand3: 'medium',
  spacingInset_shorthand4: 'medium large',
};

// Breakpoint props (ported from v1 ResponsiveProps): at/below 840px => mobile.
//
// Meridian's <Responsive query="max-width"> expects each prop's breakpoints as FLAT keys that are
// CSS length strings (e.g. `'840px': value`); the `query` ("max-width") is supplied separately on
// the element. The previous shape here — `{ default, 'max-width': { 700: true } }` — was a
// mis-port: Meridian read `'max-width'` as the breakpoint, `parseFloat('max-width')` => NaN, the
// media query never matched, and `isMobile` was stuck at `false` at every viewport (bug D496001686).
// Matches v1 constant/ResponsiveProps.js (SMALL = '840px').
const MOBILE_BREAKPOINT = '840px';
const responsiveProps = {
  isMobile: { default: false, [MOBILE_BREAKPOINT]: true },
  spacingInset_shorthand2: {
    default: 'small medium',
    [MOBILE_BREAKPOINT]: 'small small',
  },
  spacingInset_shorthand3: { default: 'medium', [MOBILE_BREAKPOINT]: 'small' },
  spacingInset_shorthand4: {
    default: 'medium large',
    [MOBILE_BREAKPOINT]: 'small medium',
  },
};

const DeviceLayoutContext = React.createContext<DeviceLayout>(DEFAULT_LAYOUT);

export const useDeviceLayout = (): DeviceLayout =>
  useContext(DeviceLayoutContext);

export const DeviceLayoutProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <Responsive query="max-width" props={responsiveProps}>
    {(layout: DeviceLayout) => (
      <DeviceLayoutContext.Provider value={{ ...DEFAULT_LAYOUT, ...layout }}>
        {children}
      </DeviceLayoutContext.Provider>
    )}
  </Responsive>
);

export default DeviceLayoutContext;
